import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@ublique/components/LoadingModal'
import Tabs from '@ublique/components/Tabs'
import Button from '@ublique/components/Button'
import Tab from '@ublique/components/Tabs/Tab'

import { formatSection } from '../helpers/model-info/sectionRenderer'

import '../index.scss'
import {
  formatConditionalFormattingData,
  formatRulesData,
  getLabelMap
} from '../helpers/model-info/rulesRenderer'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { getUserRoles, SUPER_USER_ROLE } from 'services/auth/isUserAuthorized'
import { has, isEmpty } from 'lodash'
import { resetEditedModelInfoStore } from '../actions'
import { prop } from 'lodash/fp'
import { updateMultipleTranslations } from 'services/api/translations'
import { openModal } from 'components/Modal/actions'
import Icon from 'components/Icon'
import {
  setAttributeAtFieldLevel,
  setSingleAttributeToAllDetails
} from '../helpers/model-info/utils'
import { updateConfig } from 'services/api/config'
import { formatUserPreferencesData } from '../helpers/model-info/userPrefRender'

const ModelInfo = ({ data, open, close }) => {
  const {
    workingTableId: wtId,
    models,
    modelDefinition,
    documentConfig,
    modelTitle,
    modelInfoT,
    workingTableType
  } = data

  const user = useStoreSelector('user')
  const editedValues = useStoreSelector('documents', prop('editedModelInfo'))
  const userPrefMasterData = useStoreSelector(
    'userPreferences',
    prop('masterData')
  )
  const isSuperuser = getUserRoles(user)?.includes(SUPER_USER_ROLE)
  const currentLang = localStorage.getItem('i18n-lang')

  const getTranslationPath = (key) =>
    `Models.DocumentModel-${workingTableType.toLowerCase()}.${key}`

  const hasEdits = () =>
    !isEmpty(editedValues.translations) ||
    !isEmpty(editedValues.defaultValues) ||
    editedValues.itemPerPage !== undefined ||
    editedValues.detailItemsPerPage !== undefined ||
    editedValues.pivotMode !== undefined

  const saveEdits = () => {
    const editedCurrentWTModel = models[workingTableType]
    if (!isEmpty(editedValues.translations)) {
      const translationBody = editedValues.translations.map(
        ({ key, value }) => ({
          langCode: currentLang,
          path: getTranslationPath(key),
          translation: value
        })
      )
      updateMultipleTranslations(translationBody) // ACTIVATE WHEN SAVE IS READY
    }

    if (editedValues.pivotMode !== undefined) {
      if (
        (has(modelDefinition, 'enablePivot') &&
          modelDefinition.enablePivot === editedValues.pivotMode) ||
        (!has(modelDefinition, 'enablePivot') &&
          editedValues.pivotMode === false)
      ) {
        // the user has changed the toggle but the value is the same of the document model
      } else {
        // console.log("SAVING PIVOT")
        editedCurrentWTModel.enablePivot = editedValues.pivotMode
      }
    }

    if (editedValues.itemPerPage !== undefined) {
      // console.log("SAVING ITEM PER PAGE")
      editedCurrentWTModel.itemsPerPage = editedValues.itemPerPage
    }

    if (editedValues.detailItemsPerPage !== undefined) {
      // console.log("SAVING DETAILS ITEM PER PAGE")
      setSingleAttributeToAllDetails(
        editedCurrentWTModel,
        'itemsPerPage',
        editedValues.detailItemsPerPage
      )
    }

    if (!isEmpty(editedValues.defaultValues)) {
      setAttributeAtFieldLevel(
        editedCurrentWTModel,
        'value',
        editedValues.defaultValues
      )
    }

    const editedModel = { ...models, [workingTableType]: editedCurrentWTModel }
    updateConfig(editedModel, 'model')

    resetEditedModelInfoStore()
  }

  const saveModelInfoEdits = () => {
    openModal('confirmAction', {
      message: 'confirmSaveEditingModelInfo',
      onConfirm: () => {
        saveEdits()
        close()
      }
    })

    return false
  }

  const onClose = () => {
    if (hasEdits()) {
      openModal('confirmAction', {
        message: 'confirmDeleteEditingModelInfo',
        onConfirm: () => {
          resetEditedModelInfoStore()
          close()
        }
      })
    } else {
      resetEditedModelInfoStore()
      close()
    }
    return false
  }

  const { t } = useTranslation('Models')
  const documentT = (val) => {
    const toTranslate = `DocumentModel-${modelDefinition.title
      .toLowerCase()
      .replaceAll('_', '')}.${val}`
    const label = t(toTranslate)
    return label === toTranslate ? val : label
  }
  const labelMap = getLabelMap(modelDefinition.sections, documentT)
  const tablesTab = [
    {
      id: 1,
      tabTitle: modelInfoT('SECTIONS'),
      content: formatSection(
        modelDefinition,
        documentConfig,
        documentT,
        modelInfoT,
        labelMap,
        editedValues.pivotMode,
        isSuperuser
      )
    },
    {
      id: 2,
      tabTitle: modelInfoT('RULES'),
      content: formatRulesData(modelDefinition, documentT, modelInfoT, labelMap)
    },
    {
      id: 3,
      tabTitle: modelInfoT('USER_PREF'),
      content: formatUserPreferencesData({
        userPrefMasterData,
        wtId,
        labelMap,
        modelInfoT
      })
    }
  ]

  if (modelDefinition.formatting !== undefined) {
    tablesTab.push({
      id: 4,
      tabTitle: modelInfoT('COND_FORMAT'),
      content: formatConditionalFormattingData(
        modelDefinition,
        documentT,
        modelInfoT
      )
    })
  }

  return (
    <Modal
      open={open}
      size='lg'
      className='model-info-modal'
      hasScrollingContent={false}
      iconDescription={t('close')}
      modalHeading={`${modelTitle} - ${modelInfoT('MODEL_DEF')}`}
      onRequestClose={onClose}
      passiveModal
      preventCloseOnClickOutside
    >
      {isSuperuser && (
        <Button
          className='model-info-save-button'
          hasIconOnly
          tooltipAlignment='end'
          tooltipPosition='bottom'
          kind='primary'
          iconDescription={modelInfoT('SAVE')}
          key='save'
          onClick={saveModelInfoEdits}
          renderIcon={() => <Icon name='save' />}
        />
      )}

      <Tabs>
        {modelDefinition &&
          tablesTab.map(({ tabTitle, tables, id, content }) => (
            <Tab title={tabTitle} key={id}>
              <div className='tab-wrapper'>
                <div className='model-info-content'>{content}</div>
              </div>
            </Tab>
          ))}
      </Tabs>
    </Modal>
  )
}

export default ModelInfo
